import { add, parseJSON } from 'date-fns';

declare global {
  interface Window {
    LegalNoticesClient: LegalNoticesClientLibrary;
  }
}

type LegalNoticesClientConstructor = new (config?: LegalNoticesClientConfig) => LegalNoticesClient;

interface LegalNoticesClientLibrary {
  LegalNoticesClient: LegalNoticesClientConstructor;
}

interface LegalNoticesClient {
  run(): void;
  showSettings(): void;
  setLanguageCode(code: string): void;
}

interface LegalNoticesClientConfig {
  languageCode: string;
  storage: CookieSettingsStorageProvider;
}


// TODO: once MEPS has implemented root jw.org storage provider - remove custom storage provider and related code

interface CookieSettingsStorageProvider {
  get(): Promise<CookieSettings>;
  set(settings: CookieSettings): Promise<void>;
}

interface CookieSettings {
  STRICTLY_NECESSARY: boolean;
  FUNCTIONAL: boolean | undefined;
  DIAGNOSTIC: boolean | undefined;
  USAGE: boolean | undefined;
}

/**
 * Represents the categories of cookie data.
 *   From: https://docs.jworg.dev/legal-notices-client/v2-0-3/docs/api/#table-of-contents
 */
enum StorageDataTypes {
  /**
   * Strictly necessary cookies are essential for the functionality of the website or
   * app. They are always enabled.
   */
  STRICTLY_NECESSARY = 'STRICTLY_NECESSARY',

  /**
   * User preferences such as language or display preferences. These cookies are used to
   * provide a better user experience.
   */
  FUNCTIONAL = 'FUNCTIONAL',

  /**
   * Data collected when the website or app experiences an error.
   */
  DIAGNOSTIC = 'DIAGNOSTIC',

  /**
   * Data about how the website or app is used.
   */
  USAGE = 'USAGE',
}

function getCookieName(cookieDataType: StorageDataTypes): string {
  return `cookieConsent-${cookieDataType}`;
}

function getCookieRegExp(name: string): RegExp {
  const escapedName: string = name.replace(/([[\]{}()|=;+?,.*^$])/gi, '\\$1');

  return new RegExp('(?:^' + escapedName + '|;\\s*' + escapedName + ')=(.*?)(?:;|$)', 'g');
}

function safeDecodeURIComponent(encodedURIComponent: string): string {
  try {
    return decodeURIComponent(encodedURIComponent);
  } catch {
    // probably it is not uri encoded. return as is
    return encodedURIComponent;
  }
}

class LegalNoticeStorageProvider implements CookieSettingsStorageProvider {
  get(): Promise<CookieSettings> {
    return Promise.resolve({
      STRICTLY_NECESSARY: true, // STRICTLY_NECESSARY cookies are always allowed
      FUNCTIONAL: this.getStoredValue(StorageDataTypes.FUNCTIONAL),
      DIAGNOSTIC: this.getStoredValue(StorageDataTypes.DIAGNOSTIC),
      USAGE: this.getStoredValue(StorageDataTypes.USAGE),
    });
  }

  set(settings: CookieSettings): Promise<void> {
    for (const cookieDataType of Object.keys(settings) as StorageDataTypes[]) {
      const value = settings[cookieDataType];

      if (value === undefined) {
        continue;
      }

      const expiryDate = add(new Date(), { days: 365 });
      const parentDomain = location.hostname.split('.').slice(-2).join('.');
      if (cookieDataType === StorageDataTypes.STRICTLY_NECESSARY) {
        this.setCookie(getCookieName(cookieDataType), 'true', '/', expiryDate, parentDomain);
      } else {
        this.setCookie(
          getCookieName(cookieDataType),
          `${value ? 'true' : 'false'}`,
          '/',
          expiryDate,
          parentDomain
        );
      }
    }
    return Promise.resolve();
  }

  private setCookie(key: string, value: string, path: string = '/', expires?: string | Date, domain?: string): void {
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(value);

    const cookie = [`${encodedKey}=${encodedValue}`, `path=${path}`, 'SameSite=Strict'];

    if (expires) {
      const parsedExpires = expires instanceof Date ? expires : parseJSON(expires);
      const formattedExpires = parsedExpires.toUTCString();
      cookie.push(`expires=${formattedExpires}`);
    }

    if (domain) {
      cookie.push(`domain=${domain}`);
    }

    document.cookie = cookie.join(';');
  }

  getCookie(key: string): string | undefined {
    const encodedKey = encodeURIComponent(key);

    let regExp = getCookieRegExp(encodedKey);
    let result = regExp.exec(document.cookie);

    if (result == null) {
      // try unencoded
      regExp = getCookieRegExp(key);
      result = regExp.exec(document.cookie);
    }

    if (result == null) {
      // could not find
      return;
    }

    return safeDecodeURIComponent(result[1]);
  }

  private getStoredValue(cookieDataType: StorageDataTypes): boolean | undefined {
    const value = this.getCookie(getCookieName(cookieDataType));

    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    }

    return undefined;
  }
}

(() => {
  if (window?.LegalNoticesClient == null) {
    console.warn('Legal notices client not loaded to window.LegalNoticesClient.')
    return;
  }

  const storage = new LegalNoticeStorageProvider();
  const languageCode = document.getElementsByTagName('body')[0]?.getAttribute('data-langcode') ?? 'E';

  const client = new window.LegalNoticesClient.LegalNoticesClient({ storage, languageCode });

  client.run();

  document.getElementById('lnkPrivacySettings')?.addEventListener('click', () => {
    client.showSettings();
  });
})();
